import React, { useState, useEffect } from 'react';
import { RefreshCw, CheckCircle2, AlertCircle, Clock } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * DataRefreshIndicator - Shows data freshness status with last update times
 * Can be integrated into any dashboard header
 */
export default function DataRefreshIndicator({ 
  onRefresh, 
  compact = false,
  showDetails = true 
}) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [lastChecked, setLastChecked] = useState(null);

  const fetchStatus = async () => {
    const token = localStorage.getItem('qapi-token');
    if (!token) return;
    
    try {
      const res = await fetch(`${API_URL}/api/quick-wins/refresh-status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setStatus(data);
        setLastChecked(new Date());
      }
    } catch (error) {
      console.error('Failed to fetch refresh status:', error);
    }
  };

  useEffect(() => {
    fetchStatus();
    // Auto-refresh every 5 minutes
    const interval = setInterval(fetchStatus, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const handleRefresh = async () => {
    setLoading(true);
    if (onRefresh) {
      await onRefresh();
    }
    await fetchStatus();
    setLoading(false);
  };

  const getOverallStatus = () => {
    if (!status?.data_sources) return 'unknown';
    
    const now = new Date();
    const sources = status.data_sources;
    
    // Check if any source is stale (>24h old)
    const staleCount = sources.filter(s => {
      if (!s.last_update) return true;
      const updated = new Date(s.last_update);
      const hoursDiff = (now - updated) / (1000 * 60 * 60);
      return hoursDiff > 24;
    }).length;
    
    if (staleCount === 0) return 'fresh';
    if (staleCount < sources.length / 2) return 'partial';
    return 'stale';
  };

  const overallStatus = getOverallStatus();

  const formatTime = (dateStr) => {
    if (!dateStr) return 'Never';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  if (compact) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={handleRefresh}
              disabled={loading}
              className="gap-1.5"
              data-testid="data-refresh-indicator"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              {overallStatus === 'fresh' && (
                <span className="h-2 w-2 rounded-full bg-emerald-500" />
              )}
              {overallStatus === 'partial' && (
                <span className="h-2 w-2 rounded-full bg-amber-500" />
              )}
              {overallStatus === 'stale' && (
                <span className="h-2 w-2 rounded-full bg-red-500" />
              )}
            </Button>
          </TooltipTrigger>
          <TooltipContent side="bottom" className="max-w-xs">
            <div className="space-y-2">
              <p className="font-medium">Data Status: {overallStatus === 'fresh' ? 'Fresh' : overallStatus === 'partial' ? 'Partially Stale' : 'Stale'}</p>
              {status?.data_sources?.slice(0, 3).map((source, idx) => (
                <div key={idx} className="flex justify-between text-xs">
                  <span>{source.display_name}</span>
                  <span className="text-muted-foreground">{formatTime(source.last_update)}</span>
                </div>
              ))}
            </div>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return (
    <div className="flex items-center gap-3" data-testid="data-refresh-indicator">
      <div className="flex items-center gap-2">
        {overallStatus === 'fresh' && (
          <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
            <CheckCircle2 className="h-3 w-3 mr-1" />
            Data Fresh
          </Badge>
        )}
        {overallStatus === 'partial' && (
          <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
            <Clock className="h-3 w-3 mr-1" />
            Partially Stale
          </Badge>
        )}
        {overallStatus === 'stale' && (
          <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
            <AlertCircle className="h-3 w-3 mr-1" />
            Data Stale
          </Badge>
        )}
      </div>

      {showDetails && status?.data_sources && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="text-xs text-muted-foreground cursor-help underline decoration-dotted">
                {status.data_sources.length} sources
              </span>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="max-w-sm">
              <div className="space-y-2">
                {status.data_sources.map((source, idx) => (
                  <div key={idx} className="flex justify-between gap-4 text-xs">
                    <span>{source.display_name}</span>
                    <span className={!source.last_update ? 'text-red-500' : 'text-muted-foreground'}>
                      {formatTime(source.last_update)}
                    </span>
                  </div>
                ))}
              </div>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}

      <Button 
        variant="outline" 
        size="sm" 
        onClick={handleRefresh}
        disabled={loading}
        className="gap-1.5"
        data-testid="refresh-data-btn"
      >
        <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
        Refresh
      </Button>
    </div>
  );
}
